/**
 * Catalog API: products, pricing, categories, deliverables, promotions.
 * Base URL: /api/catalog (backend must expose these routes).
 */

import { getApiBase, getAuthHeaders } from "./auth";
import { getAdminOrgId } from "./admin";
import { extractApiErrorMessage } from "./auth";

const API_BASE = getApiBase();

/** Base URL for catalogue static images (backend serves at /static). */
export function getCatalogImageBase(): string {
  const base = getApiBase();
  const withoutApi = base.replace(/\/api\/?$/, "");
  return withoutApi ? `${withoutApi}/static` : "/static";
}

/** Full URL for a catalogue image path (e.g. product.image_path). */
export function getCatalogImageUrl(path: string): string {
  const p = path.startsWith("/") ? path.slice(1) : path;
  return `${getCatalogImageBase()}/${p}`;
}

async function catalogHeaders(): Promise<Record<string, string>> {
  const orgId = await getAdminOrgId();
  return getAuthHeaders({ orgId, json: true });
}

async function catalogFetch(
  path: string,
  options: RequestInit & { skipOrg?: boolean } = {}
): Promise<Response> {
  const { skipOrg, ...init } = options;
  const headers = skipOrg
    ? { ...getAuthHeaders({ json: true }), ...(init.headers as object) }
    : { ...(await catalogHeaders()), ...(init.headers as object) };
  return fetch(`${API_BASE}/catalog${path}`, { ...init, headers });
}

/** Fetch for admin promotions API (backend: /api/admin/promotions). */
async function adminPromotionsFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = { ...(await catalogHeaders()), ...(options.headers as object) };
  const p = path.startsWith("/") ? path : `/${path}`;
  return fetch(`${API_BASE}/admin/promotions${p}`, { ...options, headers });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProductType =
  | "produit"
  | "service"
  | "licence"
  | "consommable"
  | "data_processing"
  | "bundle"
  | "hardware"
  | "package"
  | "service_area"
  | "service_fixed"
  | "subscription"
  | "license_credit";

/** Prix de base facultatif du bundle (ex. frais de dossier, forfait coordination). Stocké dans operational_cost_profile.bundle_base_price. */
export interface BundleBasePrice {
  label: string;
  mode: "fixed" | "per_unit" | "variable_components";
  value: number;
  unit?: string;
}

export type ProductStatus = "active" | "inactive" | "archived";
export type ProductUsage = "sold" | "internal_consumption";
export type ProductVisibility = "internal" | "ambassador" | "end_customer" | "both";

/** Valeurs métier pour la typologie des bundles (soumission, filtrage, présentation). */
export type BundleFamily =
  | "drone_operation"
  | "drone_cartographie"
  | "agriculture_precision";

export const BUNDLE_FAMILY_LABELS: Record<BundleFamily, string> = {
  drone_operation: "Vente de drone",
  drone_cartographie: "Services à forfait",
  agriculture_precision: "Agriculture de précision",
};

export const BUNDLE_FAMILY_OPTIONS: { value: BundleFamily | ""; label: string }[] = [
  { value: "", label: "Sans famille" },
  { value: "drone_operation", label: "Vente de drone" },
  { value: "drone_cartographie", label: "Services à forfait" },
  { value: "agriculture_precision", label: "Agriculture de précision" },
];

/** Type opérationnel du service (quand product_type = service). Chaque type pourra être configuré ensuite. */
export type ServiceOperationType =
  | "standard_service"
  | "mission_drone"
  | "consulting"
  | "support";

export const SERVICE_OPERATION_TYPE_LABELS: Record<ServiceOperationType, string> = {
  standard_service: "Service standard",
  mission_drone: "Mission drone (cartographie)",
  consulting: "Conseil",
  support: "Support",
};

export const SERVICE_OPERATION_TYPE_OPTIONS: { value: ServiceOperationType | ""; label: string }[] = [
  { value: "", label: "Non défini" },
  { value: "standard_service", label: "Service standard" },
  { value: "mission_drone", label: "Mission drone (cartographie)" },
  { value: "consulting", label: "Conseil" },
  { value: "support", label: "Support" },
];

export interface Product {
  id: string;
  code: string | null;
  name: string;
  product_type: ProductType;
  status: ProductStatus;
  visibility_scope: ProductVisibility;
  description_md?: string | null;
  unit_kind?: string | null;
  default_currency?: string;
  tax_code?: string | null;
  tax_profile?: string | null;
  track_inventory?: boolean;
  product_usage?: ProductUsage;
  system?: string | null;
  tags?: string[] | null;
  ops_family?: string | null;
  minimum_charge?: number | null;
  image_path?: string | null;
  /** Famille métier du bundle (pour regroupement soumission). */
  bundle_family?: string | null;
  /** Type opérationnel du service (mission_drone, consulting, support, etc.) — quand product_type = service. */
  service_operation_type?: string | null;
  /** Optional list of image paths (if backend supports multiple images later). */
  image_paths?: string[] | null;
  [key: string]: unknown;
}

export interface ProductDetails extends Product {
  item_prices?: ItemPrice[];
  inventory?: unknown;
  default_supplier?: unknown;
  preferred_supplier_product?: unknown;
}

export interface PricingModel {
  id: string;
  product_id: string;
  mode: string;
  base_rate?: number | null;
  min_price?: number | null;
  params?: Record<string, unknown> | null;
}

export interface ItemPrice {
  id: string;
  product_id: string;
  price_book_id: string;
  price_book_code?: string;
  unit_price: number;
  currency?: string;
  min_quantity?: number | null;
  max_quantity?: number | null;
  is_active?: boolean;
  [key: string]: unknown;
}

export interface PriceBook {
  id: string;
  code: string;
  name?: string;
  [key: string]: unknown;
}

export interface Promotion {
  id: string;
  name: string;
  type?: string;
  scope?: string;
  status?: string;
  [key: string]: unknown;
}

export type PromotionStatus = string;
export type PromotionType = string;
export type PromotionScope = string;
export type StackingPolicy = string;

export interface PromotionUsage {
  promotion_id: string;
  usage_count?: number;
  [key: string]: unknown;
}

export interface CatalogCategoryOut {
  id: string;
  name: string;
  slug?: string;
  kind?: string;
  [key: string]: unknown;
}

export type DeliverableType = "customer" | "supplier";

export interface ProductDeliverable {
  id: string;
  product_id: string;
  name: string;
  code?: string | null;
  deliverable_type: DeliverableType;
  sort_order?: number | null;
  [key: string]: unknown;
}

export interface ProductProcessTemplateMapping {
  id: string;
  product_id: string;
  process_template_id: string;
  start_policy?: string | null;
  payment_ratio_threshold?: number | null;
  [key: string]: unknown;
}

export interface BundleItem {
  product_id: string;
  quantity?: number;
  [key: string]: unknown;
}

export interface ProductSupplierProduct {
  id: string;
  product_id: string;
  supplier_id: string;
  supplier_sku?: string;
  supplier_product_name?: string | null;
  unit_cost?: number | null;
  currency?: string;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  is_preferred?: boolean;
  supplier_name?: string;
  cost_type?: string;
  [key: string]: unknown;
}

export const SUPPLIER_COST_TYPES = [
  "inventory",
  "digital_license",
  "subcontractor",
] as const;

export interface ProductSales {
  product_id: string;
  total_quantity?: number;
  total_revenue?: number;
  [key: string]: unknown;
}

export interface DefaultOperationalCostLine {
  id?: string;
  label?: string;
  cost_type?: string;
  amount?: number | null;
  required?: boolean;
  [key: string]: unknown;
}

export const INTERNAL_COST_TYPES = [
  "coordination",
  "admin",
  "supervision",
  "support",
  "quality_control",
  "other",
] as const;

export interface ProductComponentOut {
  id: string;
  product_id: string;
  child_product_id: string;
  quantity?: number;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export interface ListProductsParams {
  page?: number;
  page_size?: number;
  product_type?: string;
  status?: string;
  include_category_ids?: boolean;
  /** Requête liste allégée (payload réduit : id, code, name, product_type, status, image_path, category_ids) */
  list?: boolean;
}

export interface ListProductsResponse {
  items: Product[];
  meta?: { total: number; page: number; page_size: number };
}

export async function listProducts(
  params: ListProductsParams = {}
): Promise<ListProductsResponse> {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.page_size != null) searchParams.set("page_size", String(params.page_size));
  if (params.product_type) searchParams.set("type", params.product_type);
  if (params.status) searchParams.set("status", params.status);
  if (params.include_category_ids) searchParams.set("include_category_ids", "true");
  if (params.list) searchParams.set("list", "true");
  const res = await catalogFetch(`/products?${searchParams}`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load products"));
  return res.json();
}

export async function getProduct(id: string): Promise<Product> {
  const res = await catalogFetch(`/products/${id}`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load product"));
  return res.json();
}

export async function getProductDetails(id: string): Promise<ProductDetails> {
  const res = await catalogFetch(`/products/${id}/details`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load product details"));
  return res.json();
}

export async function createProduct(payload: Record<string, unknown>): Promise<Product> {
  const res = await catalogFetch("/products", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to create product"));
  return res.json();
}

export async function updateProduct(
  id: string,
  payload: Record<string, unknown>
): Promise<Product> {
  const res = await catalogFetch(`/products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to update product"));
  return res.json();
}

export async function deleteProduct(id: string): Promise<{ archived?: boolean; message?: string }> {
  const res = await catalogFetch(`/products/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to delete product"));
  return res.json().catch(() => ({}));
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

export async function getProductPricingModel(productId: string): Promise<PricingModel | null> {
  const res = await catalogFetch(`/products/${productId}/pricing-model`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load pricing model"));
  return res.json();
}

export async function createProductPricingModel(
  productId: string,
  payload: Record<string, unknown>
): Promise<PricingModel> {
  const res = await catalogFetch(`/products/${productId}/pricing-model`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to create pricing model"));
  return res.json();
}

export async function listItemPrices(
  productId: string,
  params?: { price_book_id?: string }
): Promise<ItemPrice[]> {
  const priceQuery = params?.price_book_id ? `?price_book_id=${params.price_book_id}` : "";
  const res = await catalogFetch(`/products/${productId}/prices${priceQuery}`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load prices"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function listPriceBooks(): Promise<PriceBook[]> {
  const res = await catalogFetch("/price-books");
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load price books"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function createItemPrice(
  productId: string,
  payload: Record<string, unknown>
): Promise<ItemPrice> {
  const res = await catalogFetch(`/products/${productId}/prices`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to create price"));
  return res.json();
}

export async function updateItemPrice(
  productId: string,
  priceId: string,
  payload: Record<string, unknown>
): Promise<ItemPrice> {
  const res = await catalogFetch(`/products/${productId}/prices/${priceId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to update price"));
  return res.json();
}

export async function deleteItemPrice(productId: string, priceId: string): Promise<void> {
  const res = await catalogFetch(`/products/${productId}/prices/${priceId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to delete price"));
}

export async function simulatePrice(params: {
  product_id?: string;
  quantity?: number;
  price_book_id?: string;
  area?: number;
  [key: string]: unknown;
}): Promise<{ unit_price?: number; total?: number; [key: string]: unknown }> {
  const res = await catalogFetch("/pricing/simulate", {
    method: "POST",
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Simulation failed"));
  return res.json();
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export async function listCategories(params?: { kind?: string }): Promise<CatalogCategoryOut[]> {
  const categoryQuery = params?.kind ? `?kind=${encodeURIComponent(params.kind)}` : "";
  const res = await catalogFetch(`/categories${categoryQuery}`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load categories"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function getProductCategories(productId: string): Promise<CatalogCategoryOut[]> {
  const res = await catalogFetch(`/products/${productId}/categories`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load product categories"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function putProductCategories(
  productId: string,
  categoryIds: string[]
): Promise<void> {
  const res = await catalogFetch(`/products/${productId}/categories`, {
    method: "PUT",
    body: JSON.stringify({ category_ids: categoryIds }),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to update product categories"));
}

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

export async function updateProductInventory(
  productId: string,
  payload: Record<string, unknown>
): Promise<unknown> {
  const res = await catalogFetch(`/products/${productId}/inventory`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to update inventory"));
  return res.json();
}

export interface InventoryListItem {
  product_id: string;
  product_name?: string;
  product_code?: string | null;
  product_type?: string;
  product_status?: string;
  unit_kind?: string | null;
  stock_quantity?: number;
  reserved_quantity?: number;
  quantity_on_order?: number;
  available_quantity?: number;
  low_stock_threshold?: number | null;
  stock_status?: string;
  stock_type?: string;
  category_labels?: string[];
  default_supplier_name?: string | null;
  [key: string]: unknown;
}

export async function listInventoryItems(params?: {
  query?: string;
  status?: string;
  product_type?: string;
  page?: number;
  page_size?: number;
}): Promise<{ items: InventoryListItem[]; meta: { total: number; page: number; page_size: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.query) searchParams.set("q", params.query);
  if (params?.status) searchParams.set("status_filter", params.status);
  if (params?.product_type) searchParams.set("product_type", params.product_type);
  if (params?.page != null) searchParams.set("page", String(params.page));
  if (params?.page_size != null) searchParams.set("page_size", String(params.page_size));
  const res = await catalogFetch(`/inventory/items?${searchParams}`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load inventory items"));
  return res.json();
}

/** List inventory movements for the registre (traceability). */
export async function listInventoryMovements(params?: {
  product_id?: string;
  movement_type?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}): Promise<{
  items: Array<{
    id: string;
    product_id: string;
    product_name: string;
    movement_type: string;
    quantity_delta: number;
    quantity_before: number | null;
    quantity_after: number | null;
    source_type: string | null;
    source_id: string | null;
    reference: string | null;
    notes: string | null;
    created_by_user_id: string | null;
    created_at: string | null;
  }>;
  meta: { total: number; page: number; page_size: number };
}> {
  const searchParams = new URLSearchParams();
  if (params?.product_id) searchParams.set("product_id", params.product_id);
  if (params?.movement_type) searchParams.set("movement_type", params.movement_type);
  if (params?.date_from) searchParams.set("date_from", params.date_from);
  if (params?.date_to) searchParams.set("date_to", params.date_to);
  if (params?.page != null) searchParams.set("page", String(params.page));
  if (params?.page_size != null) searchParams.set("page_size", String(params.page_size));
  const res = await catalogFetch(`/inventory/movements?${searchParams}`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load inventory movements"));
  return res.json();
}

// ---------------------------------------------------------------------------
// Deliverables
// ---------------------------------------------------------------------------

export async function listProductDeliverables(
  productId: string
): Promise<ProductDeliverable[]> {
  const res = await catalogFetch(`/products/${productId}/deliverables`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load deliverables"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function createProductDeliverable(
  productId: string,
  payload: Record<string, unknown>
): Promise<ProductDeliverable> {
  const res = await catalogFetch(`/products/${productId}/deliverables`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to create deliverable"));
  return res.json();
}

export async function updateProductDeliverable(
  productId: string,
  deliverableId: string,
  payload: Record<string, unknown>
): Promise<ProductDeliverable> {
  const res = await catalogFetch(`/products/${productId}/deliverables/${deliverableId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to update deliverable"));
  return res.json();
}

export async function deleteProductDeliverable(
  productId: string,
  deliverableId: string
): Promise<void> {
  const res = await catalogFetch(`/products/${productId}/deliverables/${deliverableId}`, {
    method: "DELETE",
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to delete deliverable"));
}

export async function reorderProductDeliverables(
  productId: string,
  orderedIds: string[]
): Promise<void> {
  const res = await catalogFetch(`/products/${productId}/deliverables/reorder`, {
    method: "POST",
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to reorder deliverables"));
}

// ---------------------------------------------------------------------------
// Process templates
// ---------------------------------------------------------------------------

export async function listProductProcessTemplates(
  productId: string
): Promise<ProductProcessTemplateMapping[]> {
  const res = await catalogFetch(`/products/${productId}/process-templates`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load process templates"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function createProductProcessTemplate(
  productId: string,
  payload: Record<string, unknown>
): Promise<ProductProcessTemplateMapping> {
  const res = await catalogFetch(`/products/${productId}/process-templates`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to create process template"));
  return res.json();
}

export async function updateProductProcessTemplate(
  productId: string,
  templateId: string,
  payload: Record<string, unknown>
): Promise<ProductProcessTemplateMapping> {
  const res = await catalogFetch(
    `/products/${productId}/process-templates/${templateId}`,
    { method: "PATCH", body: JSON.stringify(payload) }
  );
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to update process template"));
  return res.json();
}

export async function deleteProductProcessTemplate(
  productId: string,
  templateId: string
): Promise<void> {
  const res = await catalogFetch(`/products/${productId}/process-templates/${templateId}`, {
    method: "DELETE",
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to delete process template"));
}

// ---------------------------------------------------------------------------
// Supplier products (product-supplier link)
// ---------------------------------------------------------------------------

export async function getProductSupplierProducts(
  productId: string
): Promise<ProductSupplierProduct[]> {
  const res = await catalogFetch(`/products/${productId}/supplier-products`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load supplier products"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

// ---------------------------------------------------------------------------
// Product components (BOM)
// ---------------------------------------------------------------------------

export async function listProductComponents(
  productId: string
): Promise<ProductComponentOut[]> {
  const res = await catalogFetch(`/products/${productId}/components`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load components"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export type ProductBundleItemOut = {
  id: string;
  bundle_product_id: string;
  component_product_id: string;
  component_product_code: string | null;
  component_product_name: string;
  quantity: number | null;
  default_qty: number | null;
  qty_used: number;
  min_qty: number | null;
  max_qty: number | null;
  is_required: boolean;
  allow_override_qty: boolean;
};

export async function listProductBundleItems(productId: string): Promise<ProductBundleItemOut[]> {
  const res = await catalogFetch(`/products/${productId}/bundle-items`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load bundle items"));
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function createProductComponent(
  productId: string,
  payload: Record<string, unknown>
): Promise<ProductComponentOut> {
  const res = await catalogFetch(`/products/${productId}/components`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to add component"));
  return res.json();
}

export async function updateProductComponent(
  productId: string,
  componentId: string,
  payload: Record<string, unknown>
): Promise<ProductComponentOut> {
  const res = await catalogFetch(`/products/${productId}/components/${componentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to update component"));
  return res.json();
}

export async function deleteProductComponent(
  productId: string,
  componentId: string
): Promise<void> {
  const res = await catalogFetch(`/products/${productId}/components/${componentId}`, {
    method: "DELETE",
  });
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to remove component"));
}

// ---------------------------------------------------------------------------
// Sales (analytics)
// ---------------------------------------------------------------------------

export async function getProductSales(productId?: string): Promise<ProductSales | ProductSales[]> {
  const path = productId ? `/products/${productId}/sales` : "/products/sales";
  const res = await catalogFetch(path);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load sales"));
  return res.json();
}

// ---------------------------------------------------------------------------
// Promotions (backend: /api/admin/promotions)
// ---------------------------------------------------------------------------

export type ListPromotionsOptions = { status_filter?: string };

export async function listPromotions(options?: ListPromotionsOptions): Promise<Promotion[]> {
  const params = new URLSearchParams();
  if (options?.status_filter) params.set("status_filter", options.status_filter);
  const query = params.toString();
  const path = query ? `?${query}` : "";
  const res = await adminPromotionsFetch(path);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to load promotions"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function simulatePromotion(params: {
  product_id: string;
  quantity?: number;
  price_book_id?: string;
  area?: number;
  [key: string]: unknown;
}): Promise<{
  base_price?: number;
  promotions_applied?: Array<{ name: string; discount_amount: number }>;
  discount_amount?: number;
  final_price?: number;
  [key: string]: unknown;
}> {
  const res = await adminPromotionsFetch("/simulate", {
    method: "POST",
    body: JSON.stringify(params),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Simulation failed"));
  return res.json();
}

export async function getPromotionUsage(promotionId: string): Promise<PromotionUsage> {
  const res = await adminPromotionsFetch(`/${promotionId}/usage`);
  if (!res.ok)
    throw new Error(await extractApiErrorMessage(res, "Failed to load promotion usage"));
  return res.json();
}

export async function createPromotion(payload: Record<string, unknown>): Promise<Promotion> {
  const res = await adminPromotionsFetch("", {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to create promotion"));
  return res.json();
}

export async function updatePromotion(
  id: string,
  payload: Record<string, unknown>
): Promise<Promotion> {
  const res = await adminPromotionsFetch(`/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to update promotion"));
  return res.json();
}

export async function activatePromotion(id: string): Promise<Promotion> {
  const res = await adminPromotionsFetch(`/${id}/activate`, { method: "POST" });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to activate promotion"));
  return res.json();
}

export async function pausePromotion(id: string): Promise<Promotion> {
  const res = await adminPromotionsFetch(`/${id}/pause`, { method: "POST" });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to pause promotion"));
  return res.json();
}

export async function archivePromotion(id: string): Promise<Promotion> {
  const res = await adminPromotionsFetch(`/${id}/archive`, { method: "POST" });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Failed to archive promotion"));
  return res.json();
}
